import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource-variable/geist/wght.css';
import { IconAt, IconAutomation } from '@tabler/icons-react';
import { CalendarCheck, CalendarClock, CheckCircle2, Loader2, Mail, Settings } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import {
  draftSnapshot,
  mapCompanyToDraft,
} from '../../customer-experience/components/CxEmailSendMailModal';
import '../../customer-experience/components/CxEmailSendMailModal.css';
import {
  fetchConfig,
  fetchNotificationTypes,
  saveConfig,
  saveNotificationTypes,
  sendTestEmail,
  testConfig,
} from '../services/emailNotificationsService';
import { EmailScopeConfigModal } from './EmailScopeConfigModal';
import ProcessConfigSkeleton from '../ConfigStatusView2Skeleton';
import '../ConfigStatusView2.css';
import './EmailNotificationsConfigPage.css';

const API_BASE_URL = getApiBaseUrl();
const MAIL_API = `${API_BASE_URL}/api/vc-usr/cx/config/mail/empresas`;
const GENERAL_COD = '__geral__';

const NOTIF_ITEMS = [
  { key: 'abertura_os', label: 'Abertura da OS', icon: Mail },
  { key: 'sera_atendida_amanha', label: 'Será atendida amanhã', icon: CalendarClock },
  { key: 'sera_atendida_hoje', label: 'Será atendida hoje', icon: CalendarCheck },
  { key: 'os_concluida', label: 'OS concluída', icon: CheckCircle2 },
];

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function Pcs2TooltipHost({ text, children, align = 'start', className = '' }) {
  if (!text) return children;
  return (
    <span
      className={[
        'pcs2-tooltip-host',
        align === 'end' ? 'pcs2-tooltip-host--align-end' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-tooltip={text}
    >
      {children}
    </span>
  );
}

function Pcs2Switch({ checked, onChange, ariaLabel, disabled, compact }) {
  const inputRef = useRef(null);
  const handleChange = (e) => {
    onChange(e);
    requestAnimationFrame(() => inputRef.current?.blur());
  };
  return (
    <label className={`pcs2-switch${compact ? ' pcs2-switch--compact' : ''}`}>
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <span className="pcs2-switch-pill">
        <span className="pcs2-switch-dot" />
      </span>
    </label>
  );
}

function Pcs2ListItemSwitch({ icon: Icon, label, checked, onChange, ariaLabel }) {
  return (
    <div className="pcs2-list-item">
      <span className="pcs2-list-icon">
        <Icon size={12} strokeWidth={1.75} aria-hidden />
      </span>
      <span className="pcs2-list-label">{label}</span>
      <Pcs2Switch compact checked={checked} onChange={onChange} ariaLabel={ariaLabel} />
    </div>
  );
}

function Pcs2Subblock({ title, desc, hideHead, action, children }) {
  return (
    <div className="pcs2-subblock">
      {!hideHead && (title || desc || action) ? (
        <div className={`pcs2-subhead${action ? ' pcs2-subhead--with-action' : ''}`}>
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
          {action ? <div className="pcs2-subhead-action">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

function Pcs2Section({ icon: Icon, title, desc, children, tablerIcon = false }) {
  return (
    <section className="pcs2-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          {tablerIcon ? <Icon size={16} stroke={1.75} aria-hidden /> : <Icon size={16} strokeWidth={1.75} aria-hidden />}
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        <p className="pcs2-section-desc">{desc}</p>
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function Pcs2SegmentedControl({ value, onChange, options, ariaLabel }) {
  return (
    <div className="pcs2-segmented" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={`pcs2-segmented-btn${active ? ' pcs2-segmented-btn--active' : ''}`}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function mapGeneralToDraft(comum, hasPassword) {
  return {
    codigo: GENERAL_COD,
    nome: 'Configuração geral',
    cnpj: '',
    email: comum.email || '',
    fromEmail: '',
    senderName: '',
    smtpServer: comum.smtpServer || '',
    smtpPort: comum.smtpPort || '',
    password: '',
    hasPassword,
    configured: Boolean(comum.email && comum.smtpServer && comum.smtpPort && hasPassword),
    testEmail: comum.testEmail || '',
    testResult: comum.testResult || null,
    sendResult: comum.sendResult || null,
  };
}

export default function EmailNotificationsConfigPage() {
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailScope, setEmailScope] = useState('geral');
  const [savedScope, setSavedScope] = useState('geral');

  const [generalRow, setGeneralRow] = useState(() => mapGeneralToDraft({ email: '', smtpServer: '', smtpPort: '' }, false));
  const [generalBaseline, setGeneralBaseline] = useState('');

  const [companyRows, setCompanyRows] = useState([]);
  const [companyBaselines, setCompanyBaselines] = useState({});
  const [companyLoading, setCompanyLoading] = useState(false);
  const [expandedCodigos, setExpandedCodigos] = useState(() => new Set());
  const [testing, setTesting] = useState(null);
  const [sending, setSending] = useState(null);

  const [notifData, setNotifData] = useState({
    abertura_os: false,
    sera_atendida_amanha: false,
    sera_atendida_hoje: false,
    os_concluida: false,
    email_invoicing: '',
  });
  const [savedNotifSnapshot, setSavedNotifSnapshot] = useState('');

  const [scopeModalOpen, setScopeModalOpen] = useState(false);

  const loadGeneral = useCallback(async () => {
    const t = localStorage.getItem('authToken');
    if (!t) return;
    const d = await fetchConfig(t);
    const scope = d.comum?.email_config_scope === 'empresa' ? 'empresa' : 'geral';
    setEmailScope(scope);
    setSavedScope(scope);
    const comum = {
      email: d.comum?.email || '',
      smtpServer: d.comum?.email_smtpsv || '',
      smtpPort: d.comum?.smtpport || '',
      testEmail: '',
    };
    const row = mapGeneralToDraft(comum, Boolean(d.comum?.hasPassword));
    setGeneralRow(row);
    setGeneralBaseline(draftSnapshot(row));
  }, []);

  const loadCompanies = useCallback(async () => {
    setCompanyLoading(true);
    try {
      const res = await fetch(MAIL_API, { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
      const list = (json.data?.companies || []).map(mapCompanyToDraft);
      const baselines = {};
      for (const row of list) baselines[row.codigo] = draftSnapshot(row);
      setCompanyRows(list);
      setCompanyBaselines(baselines);
    } catch {
      setCompanyRows([]);
      setCompanyBaselines({});
    } finally {
      setCompanyLoading(false);
    }
  }, []);

  const loadNotif = useCallback(async () => {
    const t = localStorage.getItem('authToken');
    if (!t) return;
    const data = await fetchNotificationTypes(t);
    setNotifData(data);
    setSavedNotifSnapshot(JSON.stringify(data));
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadGeneral(), loadNotif(), loadCompanies()]);
      } catch (e) {
        toastError(e.message || 'Erro ao carregar configurações');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadGeneral, loadNotif, loadCompanies, toastError]);

  const patchGeneral = (patch) => {
    setGeneralRow((prev) => ({
      ...prev,
      ...patch,
      ...(patch.testResult === undefined && (patch.email || patch.password || patch.smtpServer || patch.smtpPort)
        ? { testResult: null, sendResult: null }
        : {}),
    }));
  };

  const patchCompany = (codigo, patch) => {
    const clearsResults = ['email', 'fromEmail', 'senderName', 'password', 'smtpServer', 'smtpPort'].some((k) => k in patch);
    setCompanyRows((prev) =>
      prev.map((row) =>
        row.codigo === codigo
          ? {
              ...row,
              ...patch,
              ...(clearsResults && patch.testResult === undefined ? { testResult: null, sendResult: null } : {}),
            }
          : row
      )
    );
  };

  const payloadForRow = (row) => ({
    email: row.email,
    fromEmail: row.fromEmail || '',
    senderName: row.senderName,
    password: row.password || null,
    smtpServer: row.smtpServer,
    smtpPort: parseInt(row.smtpPort, 10) || row.smtpPort,
  });

  const handleGeneralTest = async () => {
    setTesting(GENERAL_COD);
    patchGeneral({ testResult: null, sendResult: null });
    const t = localStorage.getItem('authToken');
    try {
      const r = await testConfig(t, {
        email: generalRow.email,
        password: generalRow.password || undefined,
        smtpServer: generalRow.smtpServer,
        smtpPort: generalRow.smtpPort || '587',
      });
      patchGeneral({ testResult: { success: r.success, message: r.message } });
    } catch (e) {
      patchGeneral({ testResult: { success: false, message: e.message || 'Falha no teste' } });
    } finally {
      setTesting(null);
    }
  };

  const handleGeneralSendTest = async () => {
    setSending(GENERAL_COD);
    patchGeneral({ sendResult: null });
    const t = localStorage.getItem('authToken');
    try {
      const r = await sendTestEmail(t, {
        email: generalRow.email,
        password: generalRow.password || undefined,
        smtpServer: generalRow.smtpServer,
        smtpPort: generalRow.smtpPort || '587',
        testEmail: generalRow.testEmail,
      });
      patchGeneral({ sendResult: { success: r.success, message: r.message } });
    } catch (e) {
      patchGeneral({ sendResult: { success: false, message: e.message || 'Falha ao enviar' } });
    } finally {
      setSending(null);
    }
  };

  const handleCompanyTest = async (codigo) => {
    const row = companyRows.find((r) => r.codigo === codigo);
    if (!row) return;
    setTesting(codigo);
    patchCompany(codigo, { testResult: null, sendResult: null });
    try {
      const res = await fetch(`${MAIL_API}/${encodeURIComponent(codigo)}/test`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payloadForRow(row)),
      });
      const json = await res.json().catch(() => ({}));
      patchCompany(codigo, {
        testResult: {
          success: res.ok && json.success !== false,
          message: json.message || (res.ok ? 'Conexão SMTP ok.' : `Erro ${res.status}`),
        },
      });
    } catch (e) {
      patchCompany(codigo, { testResult: { success: false, message: e.message || 'Falha no teste' } });
    } finally {
      setTesting(null);
    }
  };

  const handleCompanySendTest = async (codigo) => {
    const row = companyRows.find((r) => r.codigo === codigo);
    if (!row) return;
    setSending(codigo);
    patchCompany(codigo, { sendResult: null });
    try {
      const res = await fetch(`${MAIL_API}/${encodeURIComponent(codigo)}/send-test`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...payloadForRow(row), testEmail: row.testEmail }),
      });
      const json = await res.json().catch(() => ({}));
      patchCompany(codigo, {
        sendResult: {
          success: res.ok && json.success !== false,
          message: json.message || (res.ok ? 'E-mail enviado.' : `Erro ${res.status}`),
        },
      });
    } catch (e) {
      patchCompany(codigo, { sendResult: { success: false, message: e.message || 'Falha ao enviar' } });
    } finally {
      setSending(null);
    }
  };

  const dirtyCompanies = useMemo(
    () => companyRows.filter((row) => draftSnapshot(row) !== companyBaselines[row.codigo]),
    [companyRows, companyBaselines]
  );

  const generalDirty = draftSnapshot(generalRow) !== generalBaseline;
  const scopeDirty = emailScope !== savedScope;
  const notifDirty = JSON.stringify(notifData) !== savedNotifSnapshot;
  const isDirty = generalDirty || dirtyCompanies.length > 0 || scopeDirty || notifDirty;

  const handleSaveAll = async () => {
    setSaving(true);
    const t = localStorage.getItem('authToken');
    try {
      if (scopeDirty) {
        await saveConfig(t, { email_config_scope: emailScope });
        setSavedScope(emailScope);
      }

      if (emailScope === 'geral' && generalDirty) {
        await saveConfig(t, {
          type: 'comum',
          comum: {
            email: generalRow.email,
            smtpServer: generalRow.smtpServer,
            smtpPort: generalRow.smtpPort || '587',
            password: generalRow.password,
          },
          email_preference: 'comum',
        });
        setGeneralBaseline(draftSnapshot({ ...generalRow, password: '' }));
        if (generalRow.password) {
          setGeneralRow((prev) => ({ ...prev, hasPassword: true, password: '' }));
        }
      }

      if (emailScope === 'empresa' && dirtyCompanies.length > 0) {
        const res = await fetch(MAIL_API, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({
            companies: dirtyCompanies.map((row) => ({
              codigo: row.codigo,
              email: row.email,
              fromEmail: row.fromEmail || '',
              senderName: row.senderName,
              password: row.password || undefined,
              smtpServer: row.smtpServer,
              smtpPort: parseInt(row.smtpPort, 10) || null,
            })),
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
        await loadCompanies();
      }

      if (notifDirty) {
        await saveNotificationTypes(t, notifData);
        setSavedNotifSnapshot(JSON.stringify(notifData));
      }

      toastSuccess('Configuração salva com sucesso');
    } catch (e) {
      toastError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const scopeAction = (
    <div className="enotif-scope-actions">
      <Pcs2SegmentedControl
        ariaLabel="Escopo de configuração de e-mail"
        value={emailScope}
        onChange={setEmailScope}
        options={[
          { value: 'geral', label: 'Geral' },
          { value: 'empresa', label: 'Por empresa' },
        ]}
      />
      <Pcs2TooltipHost text="Configurar e-mail de envio" align="end">
        <button
          type="button"
          className="pcs2-btn-gear"
          aria-label="Configurar e-mail de envio"
          onClick={() => setScopeModalOpen(true)}
        >
          <Settings size={15} strokeWidth={1.75} />
        </button>
      </Pcs2TooltipHost>
    </div>
  );

  if (loading) {
    return <ProcessConfigSkeleton variant="email" ariaLabel="Carregando notificações por email" />;
  }

  return (
    <div className="pcs2-page enotif-page">
      <Pcs2Section
        icon={IconAt}
        tablerIcon
        title="Configuração geral"
        desc="Configuração de email geral do sistema"
      >
        <Pcs2Subblock
          title="Definição de emails por empresa"
          desc="Configure aqui o email geral ou por empresa"
          action={scopeAction}
        />
      </Pcs2Section>

      <hr className="pcs2-divider" />

      <Pcs2Section
        icon={IconAutomation}
        tablerIcon
        title="Automação de emails"
        desc="Configuração de automação de email"
      >
        <Pcs2Subblock
          title="Automação de emails de ordem de serviço"
          desc="Configure aqui em quais etapas do processo serão disparados email"
        >
          <div className="pcs2-card">
            <div className="pcs2-list">
              {NOTIF_ITEMS.map(({ key, label, icon }) => (
                <Pcs2ListItemSwitch
                  key={key}
                  icon={icon}
                  label={label}
                  checked={!!notifData[key]}
                  onChange={() => setNotifData((p) => ({ ...p, [key]: !p[key] }))}
                  ariaLabel={label}
                />
              ))}
            </div>
          </div>
        </Pcs2Subblock>

        <Pcs2Subblock
          title="Notificação ao setor de faturamento"
          desc="Email que receberá as notificações de contadores"
          action={
            <Pcs2TooltipHost
              text="Configure aqui o email que receberá os contadores de aferição"
              align="end"
              className="enotif-invoicing-tooltip-host"
            >
              <input
                type="email"
                className="enotif-invoicing-input"
                value={notifData.email_invoicing || ''}
                onChange={(e) => setNotifData((p) => ({ ...p, email_invoicing: e.target.value }))}
                placeholder="email@exemplo.com"
                aria-label="E-mail do setor de faturamento"
              />
            </Pcs2TooltipHost>
          }
        />
      </Pcs2Section>

      {Toaster}

      {createPortal(
        <button
          type="button"
          className="pcs2-save-fab"
          onClick={handleSaveAll}
          disabled={saving || !isDirty}
          aria-label="Salvar configuração"
        >
          {saving ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : null}
          {saving ? 'Salvando...' : 'Salvar configuração'}
        </button>,
        document.body
      )}

      <EmailScopeConfigModal
        open={scopeModalOpen}
        onClose={() => setScopeModalOpen(false)}
        emailScope={emailScope}
        generalRow={generalRow}
        generalBaseline={generalBaseline}
        onGeneralChange={patchGeneral}
        onGeneralTest={handleGeneralTest}
        onGeneralSendTest={handleGeneralSendTest}
        companyRows={companyRows}
        companyBaselines={companyBaselines}
        companyLoading={companyLoading}
        expandedCodigos={expandedCodigos}
        onToggleExpanded={(codigo) => {
          setExpandedCodigos((prev) => {
            const next = new Set(prev);
            if (next.has(codigo)) next.delete(codigo);
            else next.add(codigo);
            return next;
          });
        }}
        onCompanyChange={patchCompany}
        onCompanyTest={handleCompanyTest}
        onCompanySendTest={handleCompanySendTest}
        testing={testing}
        sending={sending}
      />
    </div>
  );
}
